'use client';
import React, { forwardRef, JSX, ReactNode } from 'react';
import { cn } from '@fshr/shared-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	wrapperClassName?: string;
	label?: ReactNode;
	labelStyle?: string;
	labelTextClassName?: string;
	errorMessage?: ReactNode;
	blacklistedCharacters?: string;
	icon?: JSX.Element;
	onClickIcon?: () => void;
}

const Input = forwardRef(
	(
		{
			wrapperClassName,
			className,
			readOnly,
			label,
			labelStyle,
			id,
			name,
			errorMessage,
			required,
			blacklistedCharacters,
			icon,
			onClickIcon,
			labelTextClassName,
			...props
		}: InputProps,
		ref: React.Ref<HTMLInputElement>
	) => {
		if (blacklistedCharacters) {
			props.value = props.value
				?.toString()
				.replace(new RegExp(`[${blacklistedCharacters}]`, 'g'), '');
		}
		return (
			<div
				className={cn('relative w-full', wrapperClassName, {
					'pt-200': label,
					'text-red-700': errorMessage,
					'pb-[20px] lg:pb-[28px]': !readOnly,
				})}
			>
				<div className="relative">
					<input
						{...{
							...props,
							placeholder:
								props.placeholder && required ? `${props.placeholder}*` : props.placeholder,
						}}
						className={cn(
							[
								'peer',
								'h-[47px]',
								'mt-[2px]',
								'border-black',
								'text-body2',
								'focus:outline-hidden',
								'w-full',
								'placeholder:transition-opacity',
								'placeholder:opacity-100!',
								'placeholder:duration-300!',
								'placeholder:text-black!',
								'focus:placeholder:opacity-0!',
							],
							{
								'border-b bg-surface-form px-4': !readOnly,
								'placeholder:text-red-700! border-red-700': errorMessage,
							},
							className
						)}
						ref={ref}
						id={id}
						name={name}
						readOnly={readOnly}
						aria-labelledby={label && id ? `${id}-label` : undefined}
						aria-invalid={errorMessage ? true : undefined}
						aria-describedby={errorMessage && id ? `${id}-error` : props['aria-describedby']}
					/>
					{label && (
						<label
							className={cn(
								'text-label uppercase absolute duration-300 opacity-100 left-0 top-0 -translate-y-[18px] peer-placeholder-shown:opacity-0 peer-placeholder-shown:translate-y-0 peer-focus:opacity-100 peer-focus:-translate-y-[18px]',
								labelStyle
							)}
							htmlFor={id}
							id={id && `${id}-label`}
						>
							{typeof label === 'string' ? (
								<span className={cn(labelTextClassName)}>{`${label}${required ? '*' : ''}`}</span>
							) : (
								label
							)}
						</label>
					)}

					{icon ? (
						<div
							className="absolute top-[15.5px] right-[16px] cursor-pointer"
							onClick={onClickIcon}
						>
							{icon}
						</div>
					) : null}
				</div>
				{errorMessage && (
					<span
						id={id ? `${id}-error` : undefined}
						className="absolute left-0 lg:pt-[2px] text-caption text-red-700"
						role="alert"
					>
						{errorMessage}
					</span>
				)}
			</div>
		);
	}
);

export default Input;
